import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, defer, Observable} from 'rxjs';
import {catchError, finalize, switchMap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {AbstractHttpService} from './abstract-http-service';

@Injectable({
  providedIn: 'root'
})
export class OfferRegistryProcessorSyncService extends AbstractHttpService {
  private processingGuard = new BehaviorSubject<boolean>(false);
  private processingGuardInFlightCount = 0;

  private options = {
    headers: this.contentTypeJsonHeaders,
    withCredentials: true
  };

  constructor(private httpClient: HttpClient) {
    super();
  }

  /**
   * Calls the private-offer registry sync endpoint so the backend can reconcile
   * `private_offer_data_registry` rows for all active standard offer sets for the given account.
   */
  processForStandardOfferSets(accountId: number, ruleId: number | null | undefined): Observable<void> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offers-registry/process`;
    const payload: {accountId: number; ruleId?: number} = {
      accountId
    };

    if (ruleId !== null && ruleId !== undefined) {
      payload.ruleId = ruleId;
    }

    const response = this.httpClient.post<void>(requestUrl,
      payload,
      this.options
    ).pipe(
      catchError(this.handleError)
    );

    return response;
  }

  /**
   * Calls the private-offer sync endpoint so the backend can sync
   * `private_offer_data_registry` rows for all active standard offer sets for the given account.
   */
  syncForAllStandardOfferSets(accountId: number): Observable<void> {
    const requestUrl = `//${environment.autoDomain}:${environment.autoPort}/api/offers-registry/sync`;
    const response = this.httpClient.post<void>(requestUrl,
      {
        accountId
      },
      this.options
    ).pipe(
      catchError(this.handleError)
    );

    return response;
  }

  /**
   * Calls the private-offer registry process and sync endpoints so the backend can reconcile
   * `private_offer_data_registry` rows for all active standard offer sets for the given account.
   */
  processAndSyncForStandardOfferSets(accountId: number, ruleId: number | null | undefined): Observable<void> {
    const processAndSync = defer(() => {
      this.incrementProcessingGuard();

      return this.processForStandardOfferSets(accountId, ruleId).pipe(
        switchMap(() => this.syncForAllStandardOfferSets(accountId)),
        finalize(() => {
          this.decrementProcessingGuard();
        })
      );
    });

    return processAndSync;
  }

  /**
   * Increments the processing guard count. If the guard count is 0, it will set the guard to true.
   * If the guard count is greater than 0, it will not set the guard to true.
   */
  private incrementProcessingGuard(): void {
    this.processingGuardInFlightCount = this.processingGuardInFlightCount + 1;

    if (this.processingGuardInFlightCount > 0) {
      this.processingGuard.next(true);
    }
  }

  /**
   * Decrements the processing guard count. If the guard count is 0, it will set the guard to true.
   * If the guard count is greater than 0, it will not set the guard to true.
   */
  private decrementProcessingGuard(): void {
    if (this.processingGuardInFlightCount > 0) {
      this.processingGuardInFlightCount = this.processingGuardInFlightCount - 1;
    }

    if (this.processingGuardInFlightCount === 0) {
      this.processingGuard.next(false);
    }
  }

  /**
   * Returns true if the processing guard is active.
   */
  isProcessingGuardActive(): boolean {
    return this.processingGuard.value;
  }
}
